/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Callout, Tldr, FaqBlock, InlineCTA } from "./shared";

export const HowToRankBody = ({ post, affiliateUrl, onTrack }) => (
  <>
    <p className="lead text-lg leading-relaxed text-white/80">
      Google still drives traffic, but a fast-growing slice of your future customers are asking{" "}
      <strong>ChatGPT</strong>, <strong>Perplexity</strong>, <strong>Claude</strong> and{" "}
      <strong>Google's AI Overviews</strong> instead of typing into a search box. If your site
      isn't engineered for AI engines to extract and cite, you're invisible — no matter how
      good your classic SEO is.
    </p>

    <Tldr>{post.tldr}</Tldr>

    <h2 id="what-is-ai-search">What is an AI search engine, exactly?</h2>
    <p>
      An AI search engine is any tool that synthesizes a written answer from multiple web
      sources instead of returning a list of blue links. The big ones in 2026 are:
    </p>
    <ul>
      <li><strong>ChatGPT Search</strong> (powered by Bing + curated partners)</li>
      <li><strong>Google AI Overviews</strong> (sits above classic Google results)</li>
      <li><strong>Perplexity</strong> (citation-first answer engine)</li>
      <li><strong>Claude with web search</strong> (Anthropic's research mode)</li>
      <li><strong>Brave / Kagi / You.com / Arc</strong> (AI-first smaller engines)</li>
    </ul>
    <p>
      They all do the same three things: <strong>crawl</strong> the open web, <strong>extract</strong>{" "}
      the most quotable chunks, and <strong>cite</strong> the original source. Your job is to be
      the easiest-to-extract chunk on your topic.
    </p>

    <h2 id="aeo-vs-geo">AEO vs GEO — what's the difference?</h2>
    <p>
      <strong>AEO (Answer Engine Optimization)</strong> is page-level — formatting individual posts
      so AI can lift a clean answer. <strong>GEO (Generative Engine Optimization)</strong> is
      site- and brand-level — making your entire entity recognizable across the open web.
    </p>
    <p>You need both. AEO gets you cited on a specific query. GEO makes you the default citation.</p>

    <h2 id="how-ai-engines-pick">How do AI engines pick which sources to cite?</h2>
    <p>From reverse-engineering thousands of cited pages, three signals dominate:</p>
    <ol>
      <li><strong>Extractability.</strong> Is there a 1–3 sentence answer near the top of the page, right after a question-style heading?</li>
      <li><strong>Schema clarity.</strong> Does the page declare what it is (Article, FAQPage, HowTo, Product) so the engine doesn't have to guess?</li>
      <li><strong>Entity authority.</strong> Is the publisher a recognized entity (Organization + Person JSON-LD + LinkedIn/Wikipedia <code>sameAs</code> links + branded mentions)?</li>
    </ol>

    <h2 id="seven-steps">The 7-step playbook to rank on AI search engines</h2>

    <h3>Step 1 — Allow AI crawlers in robots.txt</h3>
    <p>
      This is the #1 thing brands get wrong. If you block <code>GPTBot</code>,{" "}
      <code>Google-Extended</code>, <code>PerplexityBot</code>, <code>Anthropic-AI</code> or{" "}
      <code>CCBot</code>, AI engines literally cannot read your page to cite it.
    </p>
    <pre className="overflow-x-auto rounded-2xl border border-white/10 bg-black/40 p-4 text-xs text-white/80">
{`User-agent: GPTBot
Allow: /

User-agent: Google-Extended
Allow: /

User-agent: PerplexityBot
Allow: /

User-agent: Anthropic-AI
Allow: /

User-agent: CCBot
Allow: /`}
    </pre>

    <h3>Step 2 — Write in answer blocks</h3>
    <p>
      Open every section with a one-sentence answer, then expand. AI engines extract the first
      2–3 sentences directly after a question-style H2 or H3. Lead with the conclusion.
    </p>
    <Callout kind="do">
      <strong>Do:</strong> "How long does it take to rank on AI search? Most pages start being
      cited within 2–6 weeks of being crawled. Here's why…"
    </Callout>
    <Callout kind="dont">
      <strong>Don't:</strong> "Ranking on AI search is a fascinating topic with a long history.
      In this article we will explore…"
    </Callout>

    <h3>Step 3 — Add FAQ, HowTo and Article schema</h3>
    <p>
      Mark up your content with <strong>JSON-LD</strong>. AI engines extract from{" "}
      <code>FAQPage</code> and <code>HowTo</code> schemas verbatim — it's the closest thing to a
      direct line to their citation engine.
    </p>
    <p>
      Drop the schema into a <code>&lt;script type="application/ld+json"&gt;</code> tag in your
      page head. Validate with{" "}
      <a href="https://search.google.com/test/rich-results" target="_blank" rel="noopener noreferrer">
        Google's Rich Results Test
      </a>{" "}
      and{" "}
      <a href="https://validator.schema.org/" target="_blank" rel="noopener noreferrer">
        Schema.org Validator
      </a>.
    </p>

    <h3>Step 4 — Build your entity graph</h3>
    <p>AI engines disambiguate brands using <strong>entity graphs</strong>. Help them by publishing:</p>
    <ul>
      <li><code>Organization</code> JSON-LD with <code>name</code>, <code>logo</code>, <code>url</code></li>
      <li><code>Person</code> JSON-LD for your founder/author with <code>worksFor</code> back to the Organization</li>
      <li><code>sameAs</code> arrays linking to LinkedIn, X, Crunchbase, Wikipedia, YouTube, Substack — every other place your entity lives online</li>
    </ul>

    <h3>Step 5 — Publish llms.txt</h3>
    <p>
      Drop a plain-text <code>/llms.txt</code> at your domain root. It's the AI-era equivalent of
      <code>sitemap.xml</code>: a curated map of your most quotable pages with short
      descriptions. The{" "}
      <a href="https://llmstxt.org/" target="_blank" rel="noopener noreferrer">llms.txt spec</a>{" "}
      is the emerging standard.
    </p>

    <h3>Step 6 — Earn citations, not just links</h3>
    <p>
      AI engines weight <strong>unlinked mentions</strong> heavily. Get your brand named on
      Reddit, niche Discords, podcasts, YouTube transcripts, Substack newsletters — anywhere
      humans recommend things to other humans. Those mentions feed the training data and
      retrieval pipelines.
    </p>

    <h3>Step 7 — Update content quarterly</h3>
    <p>
      AI engines have a strong recency bias on competitive queries. Pages dated 2026 outrank
      2023 originals on the same topic, even when the older one is better. Bump publish dates,
      add a "Updated [month] 2026" line, and refresh statistics from current sources.
    </p>

    <h2 id="schemas-table">Which schemas matter most?</h2>
    <div className="my-6 overflow-hidden rounded-2xl border border-white/10">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.03] text-left text-xs uppercase tracking-wider text-white/45">
          <tr><th className="p-3">Schema type</th><th className="p-3">Use it for</th><th className="p-3">Quoted by AI engines</th></tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {[
            ["FAQPage", "Q&A blocks anywhere on the page", "★★★★★"],
            ["HowTo", "Step-by-step tutorials", "★★★★★"],
            ["Article / BlogPosting", "Editorial content", "★★★★"],
            ["Product", "Pricing / specs / reviews", "★★★★"],
            ["Organization + Person", "Brand entity & author authority", "★★★★"],
            ["ItemList", "Curated lists, comparisons", "★★★"],
            ["BreadcrumbList", "Site hierarchy signal", "★★"],
          ].map(([a, b, c]) => (
            <tr key={a}><td className="p-3 font-mono text-white/85">{a}</td><td className="p-3 text-white/70">{b}</td><td className="p-3 text-[#F09819]">{c}</td></tr>
          ))}
        </tbody>
      </table>
    </div>

    <h2 id="common-mistakes">Common mistakes that keep you invisible</h2>
    <ul>
      <li><strong>Blocking AI bots</strong> — your default Yoast/Rank Math setup may still disallow GPTBot. Audit it.</li>
      <li><strong>Burying the answer</strong> — 700 words of throat-clearing before the actual answer.</li>
      <li><strong>No author or organization markup</strong> — engines have no idea who you are.</li>
      <li><strong>Thin content</strong> — under 600 words rarely earns citations.</li>
      <li><strong>JS-rendered text</strong> — if the answer only appears after JavaScript runs, most AI crawlers won't see it.</li>
    </ul>

    <h2 id="how-soro-helps">How Soro automates all of this</h2>
    <p>
      Doing the 7 steps above on every page, forever, is full-time work for one or two people.
      That's exactly what{" "}
      <a href={affiliateUrl} target="_blank" rel="sponsored noopener noreferrer" onClick={() => onTrack("inline-mention", post.slug)}>Soro</a>{" "}
      automates: it researches the right buyer-intent keywords, drafts articles in your brand
      voice, injects FAQ / HowTo / Article schema, ships question-style headings, and
      auto-publishes daily to WordPress, Shopify, Webflow, Wix, Ghost or any CMS via webhook.
    </p>
    <p>Most users see their first AI citations within 4–6 weeks of installing it. It's the fastest way to compound on AEO + GEO without hiring an agency.</p>

    <InlineCTA
      post={post}
      location="midpost"
      affiliateUrl={affiliateUrl}
      onTrack={onTrack}
      label={{
        title: "Want Soro to do steps 1–7 for you, on autopilot?",
        sub: "Plug in your site. Soro publishes AI-ready content daily. Set it up in 60 seconds.",
        cta: "Start ranking with Soro",
      }}
    />

    <h2 id="platform-deep-dive">Platform deep dive: ChatGPT vs Perplexity vs Google AI Overviews</h2>
    <p>
      The 7 steps above are the universal foundation. But each AI engine has quirks that
      reward platform-specific moves. Here's what we've learned running this playbook on 30+
      sites since 2024.
    </p>

    <h3>ChatGPT Search — optimize for Bing first</h3>
    <p>
      ChatGPT Search runs on Bing's index. The single highest-leverage move is to{" "}
      <strong>submit your sitemap to Bing Webmaster Tools</strong> (most brands forget). Then
      verify your top 10 pages are actually indexed by Bing — not just Google. ChatGPT also
      heavily weights <em>unlinked brand mentions</em> on Reddit, Substack, and YouTube
      transcripts. Want the deep dive?{" "}
      <a href="/blog/how-to-get-cited-by-chatgpt">Read the 6-week ChatGPT citation playbook</a>.
    </p>

    <h3>Perplexity — depth and recency win</h3>
    <p>
      Perplexity's citation algorithm rewards two signals harder than ChatGPT: <strong>content
      depth</strong> (1,200+ words on cornerstone pages) and <strong>publish/update
      recency</strong> (dates within the last 6 months). Perplexity also surfaces tables and
      structured comparisons more often than ChatGPT, so build them.
    </p>

    <h3>Google AI Overviews — classic SEO still wins</h3>
    <p>
      From analyzing ~200 AI Overview citations in our category, three patterns dominate:
    </p>
    <ul>
      <li><strong>FAQPage schema</strong> appears in 78% of cited pages</li>
      <li><strong>The cited page already ranks in Google's top 10</strong> in 91% of cases</li>
      <li><strong>The cited paragraph contains the exact query phrase</strong> within the first 100 words in 83% of cases</li>
    </ul>
    <p>
      Translation: AI Overviews don't reward novel content — they reward already-ranking
      content that's been restructured for extraction. Find your top 10 ranking URLs, add FAQ
      schema, lead with the answer, and you'll show up in Overviews within weeks.
    </p>

    <h2 id="llms-template">Copy-paste llms.txt template</h2>
    <p>
      Drop this file at <code>https://yourdomain.com/llms.txt</code>. AI crawlers use it as a
      curated map of your most quotable pages.
    </p>
    <pre className="overflow-x-auto rounded-2xl border border-white/10 bg-black/40 p-4 text-xs text-white/80">
{`# Your Brand Name

> One-sentence description of what your company does and who it serves.

## Cornerstone content
- /pricing — full pricing breakdown with feature comparison
- /about — founder story, mission, team
- /case-studies — outcome data from real customers

## Blog (most quotable)
- /blog/your-pillar-guide — 2,500-word definitive guide on [topic]
- /blog/buyer-comparison — head-to-head with [competitor]
- /blog/faqs — common questions answered

## Brand
- Founded: 2024
- Headquartered: [City, Country]
- Founder: [Name] (/about/founder)
- Press: hello@yourdomain.com`}
    </pre>
    <p>
      Keep it short, declarative, and link to the pages you most want quoted. The{" "}
      <a href="https://llmstxt.org/" target="_blank" rel="noopener noreferrer">llms.txt spec</a>{" "}
      is still emerging — the brands that adopt now get a multi-month head start.
    </p>

    <h2 id="updated-for-2026">What changed in 2026 (vs the 2025 version of this guide)</h2>
    <ul>
      <li><strong>OAI-SearchBot</strong> is now a separate user-agent — explicitly allow it alongside <code>GPTBot</code>.</li>
      <li><strong>Perplexity Pages</strong> now compete with cited sources — long-form depth matters more than ever.</li>
      <li><strong>Google AI Overviews</strong> expanded to YMYL queries (health, finance) — E-E-A-T signals are now non-negotiable on those topics.</li>
      <li><strong>Profound, Otterly and Peec.ai</strong> emerged as the standard tools for tracking AI citations across engines.</li>
      <li><strong>Bing's share of ChatGPT's citation pool</strong> grew to ~85% — Bing Webmaster Tools is no longer optional.</li>
    </ul>

    <FaqBlock faq={post.faq} />

    <h2 id="key-takeaways">Key takeaways</h2>
    <ul>
      <li>AI engines reward extractable, schema-tagged answers more than long-form prose.</li>
      <li>Allow GPTBot, Google-Extended, PerplexityBot, Anthropic-AI and CCBot in robots.txt.</li>
      <li>Build an entity graph with Organization + Person JSON-LD and <code>sameAs</code> links.</li>
      <li>Publish <code>llms.txt</code> and validate every page with the Rich Results Test.</li>
      <li>Refresh content quarterly to stay in the citation pool.</li>
    </ul>
  </>
);
